import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2, CheckCircle2, BookOpen, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const STORAGE_SID = "dt_concierge_sid";
const STORAGE_PREFILL = "dt_quote_prefill";

const MEAL_PLAN_LABELS = {
  RO: "Sans repas",
  BB: "Petit-déjeuner",
  DP: "Demi-pension",
  PC: "Pension complète",
  AI: "All Inclusive",
};

const QUICK_PROMPTS = [
  "Je veux partir au soleil en novembre",
  "Lune de miel romantique pour 2",
  "Voyage en famille avec 2 enfants",
  "Hôtel 5 étoiles avec plage privée",
];

// Render assistant text with **bold** markdown
const renderMarkdown = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="text-[#1A1B18]">{p.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{p}</span>
    )
  );
};

export const ConciergeChat = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [extracted, setExtracted] = useState({});
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedQuote, setSubmittedQuote] = useState(null);
  const [pendingSeed, setPendingSeed] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Open from anywhere (hero chips, "Commencer la conversation") + optional seed message
  useEffect(() => {
    const onOpen = (e) => {
      setOpen(true);
      if (e.detail?.seed) setPendingSeed(e.detail.seed);
    };
    window.addEventListener("dt:concierge-open", onOpen);
    return () => window.removeEventListener("dt:concierge-open", onOpen);
  }, []);

  // Lazy boot: start session only when chat is opened the first time
  useEffect(() => {
    if (!open || sessionId) return;
    let cancelled = false;
    const existing = localStorage.getItem(STORAGE_SID);
    const boot = async () => {
      setBooting(true);
      try {
        const { data } = await api.post("/concierge/start", { session_id: existing || null });
        if (cancelled) return;
        localStorage.setItem(STORAGE_SID, data.session_id);
        setSessionId(data.session_id);
        setMessages((m) => m.length ? m : [{ role: "assistant", content: data.welcome }]);
        setExtracted(data.extracted || {});
        if (data.submitted_quote_id) setSubmittedQuote({ id: data.submitted_quote_id });
      } catch {
        if (!cancelled) setMessages([{ role: "assistant", content: "Le Concierge est momentanément indisponible. Réessayez dans un instant." }]);
      } finally {
        if (!cancelled) setBooting(false);
      }
    };
    boot();
    return () => { cancelled = true; };
  }, [open, sessionId]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, extracted]);

  // Once the session is booted and idle, fire the seeded opening message (from hero chips)
  useEffect(() => {
    if (!pendingSeed) return;
    // Returning visitor with an already-submitted session: start fresh so the seed applies
    if (submittedQuote) { resetConversation(); return; }
    if (sessionId && !booting && !loading && messages.length >= 1) {
      const seed = pendingSeed;
      setPendingSeed(null);
      send(seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSeed, sessionId, booting, loading, messages.length, submittedQuote]);

  const send = async (text) => {
    const value = (text ?? input).trim();
    if (!value || loading || !sessionId) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: value }]);
    setLoading(true);
    try {
      const { data } = await api.post("/concierge/message", { session_id: sessionId, message: value });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      setExtracted(data.extracted || {});
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Désolé, une erreur est survenue. Pouvez-vous reformuler ?" }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const goToQuote = () => {
    localStorage.setItem(STORAGE_PREFILL, JSON.stringify(extracted));
    setOpen(false);
    setTimeout(() => {
      const el = document.getElementById("devis");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.dispatchEvent(new CustomEvent("dt:prefill", { detail: extracted }));
    }, 200);
  };

  const submitFromChat = async () => {
    if (!sessionId || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/concierge/${sessionId}/submit`);
      setSubmittedQuote(data.quote);
      setMessages((m) => [...m, {
        role: "assistant",
        content: data.already_submitted
          ? "Votre demande avait déjà été envoyée — pas de double envoi ✦"
          : "✅ Votre demande a bien été envoyée. Un expert Deal Travel l'analyse et reviendra vers vous sous 24h.",
      }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "L'envoi a échoué. Réessayez dans un instant.";
      setMessages((m) => [...m, { role: "assistant", content: detail }]);
    } finally {
      setSubmitting(false);
    }
  };

  const resetConversation = () => {
    localStorage.removeItem(STORAGE_SID);
    setSessionId(null);
    setMessages([]);
    setExtracted({});
    setSubmittedQuote(null);
    setInput("");
    // re-trigger boot
    setBooting(false);
  };

  const isReady = !!extracted?.ready_to_submit && !submittedQuote;

  return (
    <>
      {/* Floating bubble */}
      <button
        data-testid="concierge-bubble"
        onClick={() => setOpen((o) => !o)}
        aria-label="Ouvrir le Concierge Deal Travel"
        className={cn(
          "fixed z-40 bottom-20 right-4 sm:right-6 group",
          "flex items-center gap-3 py-3 pl-3 pr-3 sm:pr-5",
          "bg-[#96836B] hover:bg-[#1A1B18] text-white",
          "shadow-[0_18px_40px_-12px_rgba(150,131,107,0.55)] hover:shadow-[0_22px_50px_-12px_rgba(26,27,24,0.55)]",
          "transition-all duration-300",
          open && "opacity-0 pointer-events-none scale-90"
        )}
        style={{ borderRadius: 9999 }}
      >
        <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
          <Sparkles className="h-4 w-4" strokeWidth={1.5} />
        </span>
        <span className="hidden sm:inline text-[11px] uppercase tracking-[0.22em] font-medium">Concierge</span>
      </button>

      {/* Chat window */}
      <div
        data-testid="concierge-window"
        className={cn(
          "fixed z-50 bottom-20 right-6 w-[min(420px,calc(100vw-2rem))] h-[min(640px,calc(100vh-6rem))]",
          "flex flex-col",
          "bg-white/85 backdrop-blur-xl border border-[#EAE8E3]",
          "shadow-[0_30px_80px_-20px_rgba(26,27,24,0.35)]",
          "transition-all duration-300 origin-bottom-right",
          open ? "scale-100 opacity-100" : "scale-95 opacity-0 pointer-events-none"
        )}
        style={{ borderRadius: 4 }}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-4 border-b border-[#EAE8E3]/80 bg-white/60">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#96836B] flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#96836B]">Concierge</p>
              <p className="font-serif-display text-lg leading-none mt-1">Deal Travel</p>
            </div>
          </div>
          <button
            data-testid="concierge-close"
            onClick={() => setOpen(false)}
            className="w-8 h-8 rounded-full hover:bg-[#FAF9F6] transition-colors flex items-center justify-center"
            aria-label="Fermer"
          >
            <X className="h-4 w-4 text-[#737373]" />
          </button>
        </header>

        {/* Messages */}
        <div ref={scrollRef} data-testid="concierge-messages" className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {booting && (
            <div className="flex items-center gap-2 text-sm text-[#737373]">
              <Loader2 className="h-4 w-4 animate-spin" /> Initialisation…
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
              <div
                className={cn(
                  "max-w-[85%] text-sm leading-relaxed whitespace-pre-wrap px-4 py-3",
                  m.role === "user"
                    ? "bg-[#1A1B18] text-white rounded-[18px] rounded-br-[4px]"
                    : "bg-[#FAF9F6] text-[#1A1B18] rounded-[18px] rounded-bl-[4px] border border-[#EAE8E3]"
                )}
              >
                {m.role === "assistant" ? renderMarkdown(m.content) : m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-[#FAF9F6] border border-[#EAE8E3] rounded-[18px] rounded-bl-[4px] px-4 py-3 flex gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#96836B] animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#96836B] animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#96836B] animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}

          {/* Quick prompts (only when conversation is fresh) */}
          {messages.length === 1 && !loading && !booting && (
            <div className="pt-2 space-y-2">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#96836B]">Inspirations</p>
              <div className="flex flex-wrap gap-2">
                {QUICK_PROMPTS.map((q) => (
                  <button
                    key={q}
                    data-testid={`quick-prompt-${q.slice(0, 12)}`}
                    onClick={() => send(q)}
                    className="text-xs px-3 py-1.5 border border-[#EAE8E3] bg-white hover:bg-[#1A1B18] hover:text-white hover:border-[#1A1B18] transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Project ready card */}
          {isReady && (
            <div data-testid="project-ready-card" className="mt-2 border border-[#1A1B18] bg-white p-5">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#96836B]">Votre projet est prêt</p>
              <dl className="mt-4 space-y-2 text-sm">
                <Row k="Destination" v={extracted.destination} />
                <Row k="Dates" v={extracted.check_in && extracted.check_out ? `${extracted.check_in} → ${extracted.check_out}` : extracted.period} />
                <Row k="Voyageurs" v={`${extracted.adults || 0} adulte(s)${extracted.children ? ` · ${extracted.children} enfant(s)` : ""}`} />
                <Row k="Budget" v={extracted.budget ? `${extracted.budget} ${extracted.currency || "EUR"}` : null} />
                {extracted.trip_type && <Row k="Type" v={extracted.trip_type} />}
                {extracted.meal_plan && <Row k="Pension" v={MEAL_PLAN_LABELS[extracted.meal_plan] || extracted.meal_plan} />}
                <Row k="Nom" v={extracted.full_name} />
                <Row k="Email" v={extracted.email} />
                {extracted.phone && <Row k="Téléphone" v={extracted.phone} />}
              </dl>
              <p className="mt-5 text-sm text-[#1A1B18]">Souhaitez-vous envoyer cette demande de devis à nos experts ?</p>
              <div className="mt-4 flex flex-col gap-2">
                <button
                  data-testid="concierge-confirm-submit"
                  onClick={submitFromChat}
                  disabled={submitting}
                  className="w-full bg-[#1A1B18] hover:bg-[#96836B] text-white px-6 py-3 text-[11px] uppercase tracking-[0.28em] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Création de votre devis en cours…</>) : "Oui, envoyer"}
                </button>
                <button
                  data-testid="concierge-edit-info"
                  onClick={() => {
                    setInput("Je souhaite modifier une information");
                    setTimeout(() => inputRef.current?.focus(), 50);
                  }}
                  className="w-full border border-[#EAE8E3] hover:border-[#1A1B18] bg-white px-6 py-3 text-[11px] uppercase tracking-[0.28em] transition-colors"
                >
                  Modifier mes informations
                </button>
                <button
                  data-testid="concierge-cta-devis"
                  onClick={goToQuote}
                  className="w-full text-[10px] uppercase tracking-[0.22em] text-[#96836B] hover:text-[#1A1B18] py-2 transition-colors"
                >
                  ou ouvrir le formulaire classique
                </button>
              </div>
            </div>
          )}

          {/* Success state */}
          {submittedQuote && (
            <div data-testid="quote-success-card" className="mt-2 border border-[#1A1B18] bg-[#FAF9F6] p-6 text-center">
              <CheckCircle2 className="h-10 w-10 text-[#96836B] mx-auto" strokeWidth={1.2} />
              <p className="font-serif-display text-2xl mt-4">Votre demande a bien été reçue</p>
              <p className="text-sm text-[#737373] mt-3 leading-relaxed">
                Merci pour votre confiance. Votre Concierge a transmis toutes les informations à votre Travel Planner.
                Nous reviendrons vers vous sous 24 à 48 heures afin d'échanger ensemble et de construire votre voyage sur-mesure.
              </p>
              {submittedQuote.id && (
                <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-[#96836B]">
                  Référence : {String(submittedQuote.id).slice(0, 8).toUpperCase()}
                </p>
              )}
              <div className="mt-6 space-y-2">
                <a href="/devis/demo-premium-devis" target="_blank" rel="noopener noreferrer"
                  data-testid="success-action-carnet"
                  className="flex items-center justify-center gap-2 w-full border border-[#EAE8E3] hover:border-[#1A1B18] bg-white px-6 py-3 text-[11px] uppercase tracking-[0.22em] transition-colors">
                  <BookOpen className="h-4 w-4 text-[#96836B]" /> Découvrir un exemple de Carnet de Voyage
                </a>
                <a href="/destinations" data-testid="success-action-destinations"
                  className="flex items-center justify-center gap-2 w-full border border-[#EAE8E3] hover:border-[#1A1B18] bg-white px-6 py-3 text-[11px] uppercase tracking-[0.22em] transition-colors">
                  <Globe className="h-4 w-4 text-[#96836B]" /> Continuer à explorer nos destinations
                </a>
                <button
                  data-testid="concierge-new-conversation"
                  onClick={resetConversation}
                  className="w-full text-[10px] uppercase tracking-[0.22em] text-[#96836B] hover:text-[#1A1B18] py-2 transition-colors"
                >
                  Démarrer une nouvelle demande
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-[#EAE8E3]/80 bg-white/70 p-3">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              data-testid="concierge-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Écrivez votre message…"
              disabled={loading || booting}
              className="flex-1 bg-transparent border-0 border-b border-[#EAE8E3] focus:border-[#1A1B18] focus:outline-none px-1 py-2 text-sm placeholder:text-[#B7B2A8]"
            />
            <button
              data-testid="concierge-send"
              onClick={() => send()}
              disabled={!input.trim() || loading || booting}
              className="w-10 h-10 bg-[#1A1B18] hover:bg-[#96836B] text-white flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Envoyer"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-[10px] text-[#96836B] mt-2 text-center tracking-wider">
            ✦ Propulsé par Claude • Réponse sous 24h pour un devis humain
          </p>
        </div>
      </div>
    </>
  );
};

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 border-b border-[#EAE8E3] pb-2 last:border-b-0">
    <dt className="text-[10px] uppercase tracking-[0.18em] text-[#96836B] pt-0.5">{k}</dt>
    <dd className="text-[#1A1B18] text-right">{v || <span className="text-[#B7B2A8]">—</span>}</dd>
  </div>
);

export default ConciergeChat;
